import React, { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";

import { dataTrigger } from "@/analytics";
import WavesBackground from "@/components/WavesBackground/WavesBackground";
import { Button } from "@/ui/Button/Button";
import { PaymentMethodMe } from "@/store/authStore";

import modalCls from "../CancelSubscription/styles.module.scss";
import cls from "./styles.module.scss";

type Props = {
  paymentMethods: PaymentMethodMe[];
  onBack: () => void;
  onAddCard: () => void | Promise<void>;
  onSelectCard: (paymentMethodId: string) => void | Promise<void>;
  onDeleteCard: (paymentMethodId: string) => void | Promise<void>;
  isCardLoading: boolean;
  selectingPaymentMethodId: string | null;
  deletingPaymentMethodId: string | null;
  error: string;
};

function buildMethodLabel(method: PaymentMethodMe) {
  return (
    [method.brand, method.last4 ? `•••• ${method.last4}` : null]
      .filter(Boolean)
      .join(" ") || "Сохраненная карта"
  );
}

export function PaymentMethodsScreen({
  paymentMethods,
  onBack,
  onAddCard,
  onSelectCard,
  onDeleteCard,
  isCardLoading,
  selectingPaymentMethodId,
  deletingPaymentMethodId,
  error,
}: Props) {
  const titleId = useId();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const pendingDeleteMethod = useMemo(
    () =>
      paymentMethods.find(
        (method) => method.payment_method_id === pendingDeleteId
      ) ?? null,
    [paymentMethods, pendingDeleteId]
  );

  const isDeleteSubmitting =
    Boolean(pendingDeleteId) && deletingPaymentMethodId === pendingDeleteId;

  const closeDeleteModal = () => {
    if (isDeleteSubmitting) return;
    setPendingDeleteId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return;
    await onDeleteCard(pendingDeleteId);
    setPendingDeleteId(null);
  };

  useEffect(() => {
    if (!pendingDeleteMethod) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDeleteModal();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pendingDeleteMethod, isDeleteSubmitting]);

  return (
    <>
      <div className={cls.screen}>
        <WavesBackground />

        <div className={cls.wrap}>
          <button
            type="button"
            className={cls.backBtn}
            onClick={onBack}
            {...dataTrigger("bkn.payment_methods.back")}
          >
            <ArrowLeft className={cls.backIcon} />
            Назад
          </button>

          <div className={cls.card}>
            <div className={cls.head}>
              <div className={cls.title}>Карта</div>
              <div className={cls.subtitle}>
                Выберите карту для автопродления или добавьте новую.
              </div>
            </div>

            <div className={cls.list}>
              {error ? <div className={cls.error}>{error}</div> : null}

              {paymentMethods.length > 0 ? (
                paymentMethods.map((method) => {
                  const expiry = [method.expiry_month, method.expiry_year]
                    .filter(Boolean)
                    .join("/");
                  const isSelecting =
                    selectingPaymentMethodId === method.payment_method_id;
                  const isDeleting =
                    deletingPaymentMethodId === method.payment_method_id;
                  const isSelectDisabled =
                    isCardLoading ||
                    Boolean(deletingPaymentMethodId) ||
                    Boolean(selectingPaymentMethodId) ||
                    method.is_selected;
                  const isDeleteDisabled =
                    isCardLoading ||
                    Boolean(selectingPaymentMethodId) ||
                    Boolean(deletingPaymentMethodId);

                  return (
                    <div key={method.payment_method_id} className={cls.itemRow}>
                      <button
                        type="button"
                        className={[
                          cls.item,
                          method.is_selected ? cls.itemSelected : "",
                          isSelecting ? cls.itemLoading : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => onSelectCard(method.payment_method_id)}
                        disabled={isSelectDisabled}
                        aria-busy={isSelecting}
                        {...dataTrigger("bkn.payment_methods.select_card")}
                      >
                        <span
                          className={[
                            cls.radio,
                            method.is_selected ? cls.radioSelected : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          aria-hidden="true"
                        >
                          <span className={cls.radioDot} />
                        </span>

                        <div className={cls.itemMeta}>
                          <div className={cls.itemTitle}>
                            <span>{buildMethodLabel(method)}</span>
                          </div>
                          {expiry ? (
                            <div className={cls.itemSub}>{expiry}</div>
                          ) : null}
                        </div>

                        <span className={cls.itemSide}>
                          {isSelecting ? (
                            <>
                              <Loader2 className={cls.loaderIcon} />
                              <span className={cls.loaderText}>Сохраняем</span>
                            </>
                          ) : null}
                        </span>
                      </button>

                      <button
                        type="button"
                        className={cls.deleteBtn}
                        onClick={() =>
                          setPendingDeleteId(method.payment_method_id)}
                        disabled={isDeleteDisabled}
                        aria-busy={isDeleting}
                        aria-label={`Удалить карту ${buildMethodLabel(method)}`}
                        {...dataTrigger("bkn.payment_methods.delete_card")}
                      >
                        {isDeleting ? (
                          <Loader2 className={cls.deleteLoader} />
                        ) : (
                          <Trash2 className={cls.deleteIcon} />
                        )}
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className={cls.empty}>
                  Пока нет сохраненной карты для автопродления.
                </div>
              )}
            </div>

            <Button
              variant="primary"
              size="md"
              onClick={onAddCard}
              disabled={
                isCardLoading ||
                Boolean(selectingPaymentMethodId) ||
                Boolean(deletingPaymentMethodId)
              }
              leftIcon={<Plus />}
              {...dataTrigger("bkn.payment_methods.add_card")}
            >
              {isCardLoading ? "Открываем..." : "Добавить карту"}
            </Button>
          </div>
        </div>
      </div>

      {pendingDeleteMethod &&
        createPortal(
          <div
            className={modalCls.overlay}
            role="presentation"
            onMouseDown={closeDeleteModal}
          >
            <div
              className={modalCls.modal}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className={modalCls.head}>
                <h2 id={titleId} className={modalCls.title}>
                  Удалить карту?
                </h2>
                <p className={modalCls.subtitle}>
                  Карта {buildMethodLabel(pendingDeleteMethod)} будет удалена из
                  сохраненных способов оплаты.
                </p>
              </div>

              <div className={modalCls.actions}>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={closeDeleteModal}
                  disabled={isDeleteSubmitting}
                  {...dataTrigger("bkn.payment_methods.delete_cancel")}
                >
                  Отмена
                </Button>

                <Button
                  variant="primary"
                  size="md"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleteSubmitting}
                  {...dataTrigger("bkn.payment_methods.delete_confirm")}
                >
                  {isDeleteSubmitting ? "Удаляем..." : "Удалить"}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
